import React, { Component, Fragment } from 'react';
import { Grid, Divider } from 'semantic-ui-react';
import { Field } from 'redux-form';

import { TitledCard } from 'Components/';
import { InputField } from 'Common/SemanticWrapper';
import {
  EditDailyDepositFieldEnum,
  EditDailyDepositInfoFieldEnum as Fields,
  EditDailyDepositAccrualsFieldEnum,
  EditDailyDepositSalesFieldEnum,
} from 'Common/FieldEnum';
import { MealTypesEnum } from 'Common/Constants/Enumeration/MealTypesEnum';
import { formatDecimal, formatToCashAllowNegative } from 'Common/FormHelper';
import { InputTypes } from '../helpers/EditDailyDepositHelpers';

class DailyDepositSummaryForm extends Component {
  getTotalSales = mealType => {
    const { formValues } = this.props;
    return formatDecimal(2)(
      formValues[EditDailyDepositFieldEnum.Sales].reduce(
        (sum, val) =>
          sum +
          parseFloat(val[EditDailyDepositSalesFieldEnum[`${mealType}Sales`]]),
        0
      )
    );
  };

  getTotalAccruals = mealType => {
    const { formValues } = this.props;
    if (!formValues || !formValues[EditDailyDepositFieldEnum.Accruals]) {
      return 0;
    }
    return formatToCashAllowNegative(
      formValues[EditDailyDepositFieldEnum.Accruals].reduce(
        (sum, val) =>
          val[EditDailyDepositAccrualsFieldEnum.TypeOfMeal] === mealType
            ? sum + parseFloat(val[EditDailyDepositAccrualsFieldEnum.Accruals])
            : sum,
        0
      )
    );
  };

  getTotalReceipts = () => {
    const { isSupper } = this.props;
    const mealTypes = isSupper
      ? [MealTypesEnum.Supper]
      : [MealTypesEnum.Breakfast, MealTypesEnum.Lunch, MealTypesEnum.Other];

    return formatToCashAllowNegative(
      mealTypes.reduce(
        (sum, type) =>
          sum +
          parseFloat(this.getTotalAccruals(type)) +
          parseFloat(this.getTotalSales(type)),
        0
      )
    );
  };

  getOverUnder = () => {
    const { formValues } = this.props;
    return formatToCashAllowNegative(
      formValues[EditDailyDepositFieldEnum.Info][Fields.SiteDeposit] -
        parseFloat(this.getTotalReceipts())
    );
  };

  componentDidUpdate(prevProps) {
    const { formValues, change } = this.props;

    if (formValues && prevProps.formValues) {
      const prefix = `${EditDailyDepositFieldEnum.Info}.`;
      const info = formValues[EditDailyDepositFieldEnum.Info];
      const prevInfo = prevProps.formValues[EditDailyDepositFieldEnum.Info];

      if (info && prevInfo) {
        if (
          info[Fields.MAXFree] !== prevInfo[Fields.MAXFree] ||
          info[Fields.MAXReduced] !== prevInfo[Fields.MAXReduced] ||
          info[Fields.MAXPaid] !== prevInfo[Fields.MAXPaid]
        ) {
          const maxFree = parseInt(info[Fields.MAXFree], 10);
          const maxReduced = parseInt(info[Fields.MAXReduced], 10);
          const maxPaid = parseInt(info[Fields.MAXPaid], 10);

          if (!(isNaN(maxFree) || isNaN(maxReduced) || isNaN(maxPaid))) {
            change(
              `${prefix}${Fields.ADMValue}`,
              formatDecimal(0)(maxFree + maxReduced + maxPaid)
            ).then(this.updateAttendanceFactor);
          }
        }
      }
    }
  }

  handleAttendanceFactorChange = (e, value) => {
    const { formValues, change } = this.props;
    const prefix = `${EditDailyDepositFieldEnum.Info}.`;
    const info = formValues[EditDailyDepositFieldEnum.Info];
    const attendanceFactor = parseFloat(value) / 100;

    change(
      `${prefix}${Fields.ADAValue}`,
      formatDecimal(0)(info[Fields.ADMValue] * attendanceFactor)
    ).then(this.updateAllowed);
  };

  handleAttendanceChange = field => (e, value) => {
    const { change } = this.props;
    const prefix = `${EditDailyDepositFieldEnum.Info}.`;
    e.preventDefault();

    change(`${prefix}${field}`, value).then(this.updateAttendanceFactor);
  };

  updateAttendanceFactor = () => {
    const { formValues, change } = this.props;
    const prefix = `${EditDailyDepositFieldEnum.Info}.`;
    const info = formValues[EditDailyDepositFieldEnum.Info];

    const attendanceFactor =
      (parseFloat(info[Fields.ADAValue]) / parseFloat(info[Fields.ADMValue])) *
      100;

    change(
      `${prefix}${Fields.AttendanceFactor}`,
      formatDecimal(2)(attendanceFactor)
    ).then(this.updateAllowed);
  };

  updateAllowed = () => {
    const { formValues, change } = this.props;
    const prefix = `${EditDailyDepositFieldEnum.Info}.`;
    const info = formValues[EditDailyDepositFieldEnum.Info];

    const attendanceFactor = parseFloat(info[Fields.AttendanceFactor]) / 100;

    const maxFree = parseInt(info[Fields.MAXFree], 10);
    const maxReduced = parseInt(info[Fields.MAXReduced], 10);
    const maxPaid = parseInt(info[Fields.MAXPaid], 10);

    change(
      `${prefix}${Fields.AllowedFree}`,
      formatDecimal(0)(maxFree * attendanceFactor)
    );
    change(
      `${prefix}${Fields.AllowedReduced}`,
      formatDecimal(0)(maxReduced * attendanceFactor)
    );
    change(
      `${prefix}${Fields.AllowedPaid}`,
      formatDecimal(0)(maxPaid * attendanceFactor)
    );
  };

  render() {
    const { isSupper, formValues } = this.props;
    const prefix = `${EditDailyDepositFieldEnum.Info}.`;
    return (
      <Fragment>
        <Grid>
          <Grid.Row>
            <Grid.Column width={10}>
              <TitledCard title={'Summary'}>
                <Grid verticalAlign={'middle'}>
                  <Grid.Row>
                    <Grid.Column width={4}>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column>
                            <Field
                              label={'ADM'}
                              component={InputField}
                              name={`${prefix}${Fields.ADMValue}`}
                              hideError
                              type={'number'}
                              numericSize={[8, 2]}
                              mode={InputTypes.Qty.mode}
                              formatOnBlur={InputTypes.Qty.formatter}
                              onChange={this.handleAttendanceChange(
                                Fields.ADMValue
                              )}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column>
                            <Field
                              label={'ADA'}
                              component={InputField}
                              name={`${prefix}${Fields.ADAValue}`}
                              hideError
                              numericSize={[8, 2]}
                              mode={InputTypes.Qty.mode}
                              formatOnBlur={InputTypes.Qty.formatter}
                              onChange={this.handleAttendanceChange(
                                Fields.ADAValue
                              )}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column>
                            <Field
                              label={'Attn. Factor'}
                              component={InputField}
                              name={`${prefix}${Fields.AttendanceFactor}`}
                              hideError
                              max={100}
                              numericSize={[5, 2]}
                              mode={InputTypes.Sales.mode}
                              formatOnBlur={InputTypes.Sales.formatter}
                              onChange={this.handleAttendanceFactorChange}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                    <Grid.Column width={12}>
                      <Grid>
                        <Grid.Row>
                          <Grid.Column width={4} />
                          <Grid.Column width={6}>
                            <label className={'text-orange'}>Max</label>
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <label className={'text-orange'}>Allowed</label>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={4} textAlign={'right'}>
                            <label className={'text-orange'}>Free</label>
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.MAXFree}`}
                              hideError
                              numericSize={[8, 2]}
                              mode={InputTypes.Qty.mode}
                              formatOnBlur={InputTypes.Qty.formatter}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.AllowedFree}`}
                              hideError
                              maxLength={8}
                              disabled
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={4} textAlign={'right'}>
                            <label className={'text-orange'}>Reduced</label>
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.MAXReduced}`}
                              hideError
                              numericSize={[8, 2]}
                              mode={InputTypes.Qty.mode}
                              formatOnBlur={InputTypes.Qty.formatter}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.AllowedReduced}`}
                              hideError
                              maxLength={8}
                              disabled
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row>
                          <Grid.Column width={4} textAlign={'right'}>
                            <label className={'text-orange'}>Paid</label>
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.MAXPaid}`}
                              hideError
                              numericSize={[8, 2]}
                              mode={InputTypes.Qty.mode}
                              formatOnBlur={InputTypes.Qty.formatter}
                              disabled={this.props.disabled}
                            />
                          </Grid.Column>
                          <Grid.Column width={6}>
                            <Field
                              label={false}
                              component={InputField}
                              name={`${prefix}${Fields.AllowedPaid}`}
                              hideError
                              maxLength={8}
                              disabled
                            />
                          </Grid.Column>
                        </Grid.Row>
                      </Grid>
                    </Grid.Column>
                  </Grid.Row>
                  {isSupper ? (
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={4} />
                        <Grid.Column width={4}>
                          <label className={'text-orange'}>Supper</label>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} textAlign={'right'}>
                          <label className={'text-orange'}>Total Sales</label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalSales(MealTypesEnum.Supper)}
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} textAlign={'right'}>
                          <label className={'text-orange'}>
                            Total Accruals
                          </label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalAccruals(MealTypesEnum.Supper)}
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <Grid.Row>
                        <Grid.Column width={4} />
                        <Grid.Column width={4}>
                          <label className={'text-orange'}>Breakfast</label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <label className={'text-orange'}>Lunch</label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          <label className={'text-orange'}>Other</label>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} textAlign={'right'}>
                          <label className={'text-orange'}>Total Sales</label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalSales(MealTypesEnum.Breakfast)}
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalSales(MealTypesEnum.Lunch)}
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalSales(MealTypesEnum.Other)}
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={4} textAlign={'right'}>
                          <label className={'text-orange'}>
                            Total Accruals
                          </label>
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalAccruals(MealTypesEnum.Breakfast)}
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalAccruals(MealTypesEnum.Lunch)}
                        </Grid.Column>
                        <Grid.Column width={4}>
                          {this.getTotalAccruals(MealTypesEnum.Other)}
                        </Grid.Column>
                      </Grid.Row>
                    </Fragment>
                  )}
                  <Grid.Row>
                    <Grid.Column>
                      <Divider />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={4} textAlign={'right'}>
                      <label className={'text-orange'}>Total Receipts</label>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      {this.getTotalReceipts()}
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={4} textAlign={'right'}>
                      <label className={'text-orange'}>Actual Deposit</label>
                    </Grid.Column>
                    <Grid.Column width={4}>
                      <Field
                        label={false}
                        component={InputField}
                        name={`${prefix}${Fields.SiteDeposit}`}
                        hideError
                        numericSize={[8, 2]}
                        mode={InputTypes.Sales.mode}
                        formatOnBlur={InputTypes.Sales.formatter}
                        disabled={this.props.disabled}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column width={4} textAlign={'right'}>
                      <label className={'text-orange'}>Over/Under</label>
                    </Grid.Column>
                    <Grid.Column width={4}>{this.getOverUnder()}</Grid.Column>
                  </Grid.Row>
                </Grid>
              </TitledCard>
            </Grid.Column>

            {// Only allow this section to be edited/shown When primary and secondary site are the same
            formValues[
              `${EditDailyDepositFieldEnum.Info}.${Fields.PrimarySiteId}`
            ] ===
              formValues[
                `${EditDailyDepositFieldEnum.Info}.${Fields.SiteId}`
              ] && (
              <Grid.Column width={6}>
                <TitledCard title={'Serving Site'}>
                  <Grid>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <Field
                          label={'Misc 1'}
                          component={InputField}
                          name={`${prefix}${Fields.Misc1Amount}`}
                          hideError
                          numericSize={[8, 2]}
                          mode={InputTypes.Sales.mode}
                          formatOnBlur={InputTypes.Sales.formatter}
                          disabled={this.props.disabled}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <Field
                          label={'Misc 2'}
                          component={InputField}
                          name={`${prefix}${Fields.Misc2Amount}`}
                          hideError
                          numericSize={[8, 2]}
                          mode={InputTypes.Sales.mode}
                          formatOnBlur={InputTypes.Sales.formatter}
                          disabled={this.props.disabled}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <Field
                          label={'Labor Hours'}
                          component={InputField}
                          name={`${prefix}${Fields.LabourHours}`}
                          hideError
                          numericSize={[8, 2]}
                          mode={InputTypes.Sales.mode}
                          formatOnBlur={InputTypes.Sales.formatter}
                          disabled={this.props.disabled}
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column>
                        <Field
                          label={'Comments'}
                          component={InputField}
                          name={`${prefix}${Fields.Comments}`}
                          hideError
                          maxLength={255}
                          disabled={this.props.disabled}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </TitledCard>
              </Grid.Column>
            )}
          </Grid.Row>
        </Grid>
      </Fragment>
    );
  }
}

export { DailyDepositSummaryForm };



// WEBPACK FOOTER //
// ./src/pages/modifydailydeposits/components/SummaryForm.js